import {
  useCallback,
  useEffect,
  useState,
  type PropsWithChildren,
} from "react";
import { useSearchParams } from "react-router-dom-v5-compat";
import type { ChatPropertiesType } from "@b2bportal/chat-api";
import type { ChatTransfer } from "@b2bportal/ivr-api";
import {
  ChatSupportContext,
  type IChatSupportContext,
  KustomerChatSupportContext,
  type IKustomerChatSupportContext,
} from "../../contexts";
import {
  createGeneralSupportConversation,
  createSupportConversationForProduct,
  initializeKustomerChat,
  listenForClose,
  listenForOpen,
  listenForUnreadCountUpdates,
  loginKustomer,
  openKustomerChat,
  openKustomerConversationById,
  startKustomerChat,
} from "./kustomerChat";

export interface IKustomerConfigurationProps {
  apiKey?: string;
  brandId?: string;
  enabled: boolean;
  hopperUserId?: string;
  kustomerAccessToken?: string;
  tenant: string;
  forceUserLanguage?: string;
}

export interface IChatSupportProvider {
  config: IKustomerConfigurationProps;
}

export const KustomerChatSupportProvider = ({
  children,
  config,
}: PropsWithChildren<IChatSupportProvider>) => {
  const [queryParams] = useSearchParams();
  const [hopperUserId, setHopperUserId] = useState(config.hopperUserId);
  const [isLoading, setIsLoading] = useState(false);
  const [kustomerAccessToken, setKustomerAccessToken] = useState(
    config.kustomerAccessToken
  );
  const [loaded, setLoaded] = useState(false);
  const [started, setStarted] = useState(false);
  const [opened, setOpened] = useState(false);
  const [isChatCollapsed, setIsChatCollapsed] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  const openChatWindow = useCallback(() => {
    openKustomerChat();
  }, []);

  const openGeneralSupportChat = useCallback(async () => {
    await createGeneralSupportConversation(config.tenant);
  }, [config.tenant]);

  const openProductSpecificSupportChat = useCallback(
    async (
      productId: string,
      productType: ChatPropertiesType,
      requestType: string,
      _chatTransfer?: ChatTransfer // Unused currently
    ) => {
      await createSupportConversationForProduct(
        productId,
        productType,
        requestType
      );
    },
    []
  );

  const onStartedChat = useCallback(() => {
    setStarted(true);
    listenForUnreadCountUpdates((count) => {
      setUnreadCount(count);
    });

    listenForClose(() => {
      setOpened(false);
      setIsChatCollapsed(true);
    });

    listenForOpen(() => {
      setOpened(true);
      setIsChatCollapsed(false);
    });
  }, []);

  const onLoadedChat = useCallback(() => {
    setIsLoading(false);
    setLoaded(true);
    if (!started) {
      startKustomerChat(
        {
          brandId: config.brandId,
          hideChatIcon: true,
          hideNewConversationButtonOnHistory: true,
          hideNewConversationButtonOnEnd: true,
          lang: config.forceUserLanguage,
        },
        onStartedChat
      );
    }
  }, [config, onStartedChat, started]);

  useEffect(() => {
    const { apiKey, enabled } = config;

    if (!loaded && !isLoading && apiKey && enabled) {
      setIsLoading(true);
      initializeKustomerChat(
        {
          apiKey,
          enabled,
        },
        onLoadedChat
      );
    }
  }, [isLoading, config, loaded, onLoadedChat]);

  useEffect(() => {
    if (started) {
      if (hopperUserId && kustomerAccessToken) {
        loginKustomer(hopperUserId, kustomerAccessToken);
      }
    }
  }, [hopperUserId, kustomerAccessToken, started]);

  useEffect(() => {
    if (started) {
      const showSupportChat = queryParams.has("chat-support");
      const openConversationId = queryParams.get("openConversation");

      if (openConversationId) {
        openKustomerConversationById(openConversationId);
      } else if (showSupportChat) {
        createGeneralSupportConversation(config.tenant);
      }
    }
  }, [config.tenant, queryParams, started]);

  useEffect(() => {
    if (!hopperUserId && config.hopperUserId) {
      setHopperUserId(config.hopperUserId);
    }

    if (!kustomerAccessToken && config.kustomerAccessToken) {
      setKustomerAccessToken(config.kustomerAccessToken);
    }
  }, [hopperUserId, kustomerAccessToken, config]);

  const supportContext: IChatSupportContext = {
    openChatWindow,
    generalSupportChat: openGeneralSupportChat,
    productSupportChat: openProductSpecificSupportChat,
  };

  const kustomerSupportContext: IKustomerChatSupportContext = {
    setHopperUserId,
    setKustomerAccessToken,
    unreadCount,
    isOpened: opened,
    isChatCollapsed,
  };

  return (
    <ChatSupportContext.Provider value={supportContext}>
      <KustomerChatSupportContext.Provider value={kustomerSupportContext}>
        {children}
      </KustomerChatSupportContext.Provider>
    </ChatSupportContext.Provider>
  );
};
